/**
 * NumberCart — Review cart and purchase ALL numbers in one go
 *
 * Flow:
 *  1. User reviews cart items (with live recalculated prices from backend)
 *  2. Selects ONE identity for all purchases
 *  3. Clicks "Purchase All" → buys every item sequentially, shows progress
 *  4. Successfully purchased numbers are removed from cart automatically
 */

import React, { useEffect, useState } from "react";
import { useDispatch, useSelector } from "react-redux";
import { useNavigate } from "react-router-dom";
import {
  Table, Button, Tag, Modal, Select, Tooltip, Spin, Empty, Progress,
  Popconfirm,
} from "antd";
import ReactCountryFlag from "react-country-flag";
import {
  fetchCart,
  removeFromCart,
  clearCart,
  removePurchasedFromCart,
} from "../../../core/data/redux/slices/numberCartSlice";
import { purchaseInventoryNumber } from "../../../core/data/redux/slices/numberInventorySlice";
import { fetchIdentities }         from "../../../core/data/redux/slices/kycDocumentSlice";
import { Link }                    from "react-router-dom";
import { all_routes }              from "../../router/all_routes";
import { buildDocList }            from "./BrowseNumbers";

const { Option } = Select;
const route = all_routes;

// ── Helpers ───────────────────────────────────────────────────────────────────
const Flag = ({ code, size = 28, style = {} }) =>
  code ? (
    <ReactCountryFlag
      countryCode={code}
      svg
      style={{ width: size, height: size * 0.75, borderRadius: 3, objectFit: "cover", ...style }}
      title={code}
    />
  ) : (
    <span style={{ fontSize: size * 0.8, lineHeight: 1 }}>🌐</span>
  );

// ── Docs Modal ────────────────────────────────────────────────────────────────
const DocsModal = ({ open, docs, number, countryName, onClose }) => {
  const fullList = buildDocList(docs || [], countryName);
  return (
    <Modal
      title={null}
      open={open}
      onCancel={onClose}
      footer={<Button onClick={onClose} style={{ borderRadius: 8 }}>Close</Button>}
      width={460}
      destroyOnClose
    >
      <div style={{
        background: "linear-gradient(135deg, #3C2371, #8e77fa)",
        borderRadius: "8px 8px 0 0", padding: "18px 24px",
        margin: "-20px -24px 20px", color: "#fff",
      }}>
        <h5 style={{ color: "#fff", margin: 0, fontWeight: 700 }}>
          <i className="ti ti-file-certificate" style={{ marginRight: 8 }} />Required Documents
        </h5>
        {number && <p style={{ color: "rgba(255,255,255,0.7)", margin: "4px 0 0", fontSize: 12 }}>To purchase +{number}</p>}
      </div>

      {fullList.length === 0 ? (
        <div style={{ textAlign: "center", padding: "20px 0", color: "#52c41a" }}>
          <i className="ti ti-circle-check" style={{ fontSize: 36, display: "block", marginBottom: 8 }} />
          <p style={{ fontWeight: 600, margin: 0 }}>No documents required</p>
          <p style={{ color: "#8c8c8c", fontSize: 12, marginTop: 4 }}>This number can be purchased without uploading any documents.</p>
        </div>
      ) : (
        <>
          <p style={{ color: "#595959", fontSize: 13, marginBottom: 14 }}>
            Submit these in <strong>KYC Documents</strong> before purchasing:
          </p>
          {fullList.map((d, i) => (
            <div key={d.type ?? i} style={{
              display: "flex", alignItems: "flex-start", gap: 12,
              padding: "10px 14px", marginBottom: 8,
              background: "#f8f9ff", borderRadius: 8, border: "1px solid #e8edff",
            }}>
              <div style={{
                background: "linear-gradient(135deg,#3C2371,#8e77fa)",
                color: "#fff", borderRadius: "50%",
                width: 26, height: 26, flexShrink: 0,
                display: "flex", alignItems: "center", justifyContent: "center",
                fontSize: 11, fontWeight: 700,
              }}>{i + 1}</div>
              <div style={{ flex: 1 }}>
                <div style={{ fontWeight: 600, fontSize: 13, color: "#1a1a2e" }}>{d.label}</div>
                {d.type === 1 && <div style={{ fontSize: 11, color: "#8c8c8c", marginTop: 2 }}>Examples: Passport, national ID, driver's license</div>}
                {d.type === 2 && <div style={{ fontSize: 11, color: "#8c8c8c", marginTop: 2 }}>Examples: Utility bill, bank statement (within 90 days)</div>}
                {d.type === 3 && <div style={{ fontSize: 11, color: "#fa8c16", marginTop: 2 }}>Must be issued in {countryName || "the local country"}</div>}
              </div>
            </div>
          ))}
        </>
      )}
    </Modal>
  );
};

// ── Bulk Purchase Modal ───────────────────────────────────────────────────────
// Single identity selector → purchases ALL items in one go with progress
const BulkPurchaseModal = ({ open, items, onClose }) => {
  const dispatch  = useDispatch();
  const { identities, identitiesLoading } = useSelector((s) => s.kycDocuments);

  const [identityId, setIdentityId]     = useState(null);
  const [purchasing,  setPurchasing]    = useState(false);
  const [progress,    setProgress]      = useState({ done: 0, total: 0, errors: [], successes: [] });
  const [finished,    setFinished]      = useState(false);

  const unpurchased = items; // all items still in cart

  const reset = () => {
    setIdentityId(null);
    setPurchasing(false);
    setProgress({ done: 0, total: 0, errors: [], successes: [] });
    setFinished(false);
  };

  const handleClose = () => { reset(); onClose(progress.successes); };

  const handlePurchaseAll = async () => {
    if (!identityId || !unpurchased.length) return;
    setPurchasing(true);
    setProgress({ done: 0, total: unpurchased.length, errors: [], successes: [] });

    const errors    = [];
    const successes = [];

    for (const item of unpurchased) {
      try {
        const result = await dispatch(purchaseInventoryNumber({
          did_id:         item.did_id,
          number:         item.number,
          monthly_fee:    item.monthly_fee   || 0,
          activation_fee: item.activation    || 0,
          channels:       item.channels      || 1,
          country:        item.country,
          area:           item.city          || "",
          identity_id:    identityId,
        }));

        if (result.error) {
          errors.push({ number: item.number, message: result.payload || "Purchase failed" });
        } else {
          successes.push(item.did_id);
          // Remove from DB cart immediately
          dispatch(removePurchasedFromCart(item.did_id));
        }
      } catch (e) {
        errors.push({ number: item.number, message: e.message });
      }

      setProgress((prev) => ({
        ...prev,
        done: prev.done + 1,
        errors,
        successes,
      }));
    }

    setPurchasing(false);
    setFinished(true);
  };

  const pct = progress.total > 0 ? Math.round((progress.done / progress.total) * 100) : 0;

  return (
    <Modal
      title={null}
      open={open}
      onCancel={purchasing ? undefined : finished ? handleClose : () => { reset(); onClose([]); }}
      footer={null}
      width={560}
      maskClosable={false}
      destroyOnClose
      closable={!purchasing}
      closeIcon={
        <span style={{
          color: "#fff", fontSize: 18, lineHeight: 1,
          display: "flex", alignItems: "center", justifyContent: "center",
          width: 24, height: 24,
        }}>✕</span>
      }
    >
      {/* Inject style to remove hover background from the Ant Design close button */}
      <style>{`
        .ant-modal-close:hover { background: transparent !important; box-shadow: none !important; }
        .ant-modal-close { top: 18px !important; right: 18px !important; }
      `}</style>
      {/* Header */}
      <div style={{
        background: "linear-gradient(135deg, #3C2371, #8e77fa)",
        borderRadius: "8px 8px 0 0", padding: "20px 24px",
        margin: "-20px -24px 24px", color: "#fff",
      }}>
        <h5 style={{ color: "#fff", margin: 0, fontWeight: 700 }}>
          <i className="ti ti-shopping-cart" style={{ marginRight: 8 }} />
          Purchase {unpurchased.length} Number{unpurchased.length !== 1 ? "s" : ""}
        </h5>
        <p style={{ color: "rgba(255,255,255,0.7)", margin: "4px 0 0", fontSize: 12 }}>
          All numbers will be purchased using the identity you select below
        </p>
      </div>

      {/* Identity — with clear explanation */}
      {!purchasing && !finished && (
        <>
          {/* What is an Identity? */}
          {/* <div style={{
            background: "#f0f5ff", border: "1px solid #adc6ff",
            borderRadius: 10, padding: "12px 16px", marginBottom: 18,
            fontSize: 13,
          }}>
            <p style={{ fontWeight: 700, color: "#1d3557", margin: "0 0 4px" }}>
              <i className="ti ti-id-badge" style={{ marginRight: 6, color: "#2f54eb" }} />What is an Identity?
            </p>
            <p style={{ color: "#4a6fa5", margin: 0, lineHeight: 1.6 }}>
              An <strong>Identity</strong> is a set of KYC documents linked to a specific end-user or business.
              When a number requires verification, you select the Identity whose documents cover that user.
              Manage identities and their documents in{" "}
              <strong>KYC Documents</strong>.
            </p>
          </div> */}

          <div style={{ marginBottom: 20 }}>
            <label style={{ fontWeight: 600, display: "block", marginBottom: 6, fontSize: 13 }}>
              Select Identity for all purchases <span style={{ color: "#cf1322" }}>*</span>
            </label>
            <Select
              showSearch
              placeholder={identitiesLoading ? "Loading identities…" : "Select the identity to use for all numbers…"}
              style={{ width: "100%" }}
              size="large"
              value={identityId}
              onChange={setIdentityId}
              loading={identitiesLoading}
              filterOption={(input, option) =>
                option?.searchlabel?.toLowerCase().includes(input.toLowerCase())
              }
              optionLabelProp="label"
            >
              {identities.map((id) => (
                <Option key={id.id} value={id.id} label={id.name} searchlabel={id.name}>
                  <div style={{ display: "flex", alignItems: "center", justifyContent: "space-between" }}>
                    <span style={{ fontWeight: 500 }}>{id.name}</span>
                    <Tag color={id.persistence_status === "active" ? "green" : "default"} style={{ marginLeft: 8 }}>
                      {id.persistence_status || "active"}
                    </Tag>
                  </div>
                </Option>
              ))}
            </Select>

            {!identitiesLoading && identities.length === 0 && (
              <div style={{
                marginTop: 8, padding: "10px 14px",
                background: "#fff7e6", border: "1px solid #ffd591",
                borderRadius: 8, fontSize: 12, color: "#613400",
              }}>
                <i className="ti ti-alert-triangle" style={{ marginRight: 6 }} />
                No identities found. Create one in{" "}
                <Link to={all_routes.didKycDocuments} style={{ color: "#613400", fontWeight: 700, textDecoration: "underline" }}>
                  KYC Documents
                </Link>{" "}
                first.
              </div>
            )}
          </div>

          {/* Numbers being purchased */}
          <div style={{ marginBottom: 20, maxHeight: 200, overflowY: "auto" }}>
            {unpurchased.map((item) => (
              <div key={item.did_id} style={{
                display: "flex", justifyContent: "space-between", alignItems: "center",
                padding: "8px 12px", marginBottom: 6,
                background: "#f8f9ff", borderRadius: 8, border: "1px solid #e8edff",
              }}>
                <div style={{ display: "flex", alignItems: "center", gap: 10 }}>
                  <Flag code={item.country_short_name} size={24} />
                  <span style={{ fontFamily: "monospace", fontWeight: 700, fontSize: 13 }}>+{item.number}</span>
                  {item.city && <span style={{ color: "#8c8c8c", fontSize: 11 }}>· {item.city}</span>}
                </div>
                <span style={{ fontWeight: 700, color: "#52c41a", fontSize: 13 }}>
                  ${Number(item.ourMonthlyPrice || item.monthly_fee || 0).toFixed(2)}/mo
                </span>
              </div>
            ))}
          </div>

          {/* Totals */}
          <div style={{
            display: "grid", gridTemplateColumns: "1fr 1fr",
            gap: 10, marginBottom: 20,
            background: "#f8f9ff", borderRadius: 10, padding: "12px 16px",
            border: "1px solid #e8edff",
          }}>
            <div style={{ textAlign: "center" }}>
              <div style={{ fontSize: 11, color: "#8c8c8c", marginBottom: 2 }}>TOTAL ACTIVATION</div>
              <div style={{ fontWeight: 700, fontSize: 18, color: "#3C2371" }}>
                ${unpurchased.reduce((s, i) => s + (i.ourActivationPrice || i.activation || 0), 0).toFixed(2)}
              </div>
            </div>
            <div style={{ textAlign: "center" }}>
              <div style={{ fontSize: 11, color: "#8c8c8c", marginBottom: 2 }}>TOTAL MONTHLY</div>
              <div style={{ fontWeight: 700, fontSize: 18, color: "#52c41a" }}>
                ${unpurchased.reduce((s, i) => s + (i.ourMonthlyPrice || i.monthly_fee || 0), 0).toFixed(2)}/mo
              </div>
            </div>
          </div>

          <div style={{ display: "flex", gap: 10 }}>
            <Button onClick={() => { reset(); onClose([]); }} style={{ borderRadius: 8, flex: 1 }}>Cancel</Button>
            <Button
              type="primary"
              disabled={!identityId}
              onClick={handlePurchaseAll}
              style={{
                borderRadius: 8, flex: 2,
                background: identityId ? "linear-gradient(135deg, #3C2371, #8e77fa)" : undefined,
                border: "none", fontWeight: 700, fontSize: 14,
              }}
            >
              <i className="ti ti-shopping-cart" style={{ marginRight: 6 }} />
              Purchase All {unpurchased.length} Numbers
            </Button>
          </div>
        </>
      )}

      {/* Progress */}
      {purchasing && (
        <div style={{ textAlign: "center", padding: "20px 0" }}>
          <Progress
            type="circle"
            percent={pct}
            strokeColor={{ "0%": "#3C2371", "100%": "#8e77fa" }}
            style={{ marginBottom: 16 }}
          />
          <p style={{ fontWeight: 600, fontSize: 15, color: "#1a1a2e" }}>
            Purchasing {progress.done} of {progress.total}…
          </p>
          <p style={{ color: "#8c8c8c", fontSize: 12 }}>Please wait, do not close this window</p>
        </div>
      )}

      {/* Results */}
      {finished && (
        <div>
          {progress.successes.length > 0 && (
            <div style={{
              background: "#f6ffed", border: "1px solid #b7eb8f",
              borderRadius: 10, padding: "14px 18px", marginBottom: 16,
            }}>
              <p style={{ fontWeight: 700, color: "#237804", margin: "0 0 6px", fontSize: 14 }}>
                <i className="ti ti-circle-check" style={{ marginRight: 6 }} />
                {progress.successes.length} number{progress.successes.length !== 1 ? "s" : ""} purchased successfully!
              </p>
              <p style={{ color: "#52c41a", margin: 0, fontSize: 12 }}>
                They will appear in <strong>My Numbers</strong> once activated.
              </p>
            </div>
          )}

          {progress.errors.length > 0 && (
            <div style={{
              background: "#fff1f0", border: "1px solid #ffa39e",
              borderRadius: 10, padding: "14px 18px", marginBottom: 16,
            }}>
              <p style={{ fontWeight: 700, color: "#a8071a", margin: "0 0 8px", fontSize: 14 }}>
                <i className="ti ti-alert-circle" style={{ marginRight: 6 }} />
                {progress.errors.length} purchase{progress.errors.length !== 1 ? "s" : ""} failed
              </p>
              {progress.errors.map((e, i) => (
                <div key={i} style={{ fontSize: 12, color: "#cf1322", marginTop: 4 }}>
                  <strong>+{e.number}</strong>: {e.message}
                </div>
              ))}
            </div>
          )}

          <Button
            type="primary"
            onClick={handleClose}
            style={{
              width: "100%", borderRadius: 8,
              background: "linear-gradient(135deg, #3C2371, #8e77fa)",
              border: "none", fontWeight: 600,
            }}
          >
            Done
          </Button>
        </div>
      )}
    </Modal>
  );
};

// ─────────────────────────────────────────────────────────────────────────────
// Main NumberCart Component
// ─────────────────────────────────────────────────────────────────────────────
const NumberCart = () => {
  const dispatch = useDispatch();
  const navigate = useNavigate();

  const { items, loading, removing } = useSelector((s) => s.numberCart);

  const [bulkModalOpen,    setBulkModalOpen]    = useState(false);
  const [docsModal,        setDocsModal]        = useState({ open: false, docs: [], number: "", countryName: "" });
  const [purchasedIds,     setPurchasedIds]     = useState(new Set());
  // Checkbox selection — keys are did_id values
  const [selectedRowKeys,  setSelectedRowKeys]  = useState([]);

  useEffect(() => {
    dispatch(fetchCart());
    dispatch(fetchIdentities());
  }, [dispatch]);

  // When cart loads/changes, auto-select all unpurchased items
  useEffect(() => {
    const unpurchasedKeys = items
      .filter((i) => !purchasedIds.has(i.did_id))
      .map((i) => i.did_id);
    setSelectedRowKeys(unpurchasedKeys);
  }, [items]); // eslint-disable-line react-hooks/exhaustive-deps

  const handleBulkClose = (successIds) => {
    setBulkModalOpen(false);
    if (successIds.length > 0) {
      const successSet = new Set(successIds);
      setPurchasedIds((prev) => new Set([...prev, ...successIds]));
      // Deselect purchased rows
      setSelectedRowKeys((prev) => prev.filter((k) => !successSet.has(k)));
      // Refresh cart from server
      dispatch(fetchCart());
    }
  };

  const unpurchasedItems = items.filter((i) => !purchasedIds.has(i.did_id));
  // Items the user has checked and wants to buy
  const selectedItems = unpurchasedItems.filter((i) => selectedRowKeys.includes(i.did_id));

  const totalActivation = selectedItems.reduce((s, i) => s + (i.ourActivationPrice || i.activation || 0), 0);
  const totalMonthly    = selectedItems.reduce((s, i) => s + (i.ourMonthlyPrice    || i.monthly_fee  || 0), 0);

  const columns = [
    {
      title: "Phone Number",
      key: "number",
      render: (_, r) => (
        <div style={{ display: "flex", alignItems: "center", gap: 10 }}>
          <Flag code={r.country_short_name} size={28} style={{ borderRadius: 3 }} />
          <div>
            <div style={{ fontFamily: "monospace", fontWeight: 700, fontSize: 14, color: "#1a1a2e" }}>+{r.number}</div>
            <div style={{ fontSize: 11, color: "#8c8c8c" }}>{r.country}{r.city ? ` · ${r.city}` : ""}</div>
          </div>
        </div>
      ),
    },
    {
      title: "Channels",
      dataIndex: "channels",
      align: "center",
      render: (v) => <span style={{ fontWeight: 600, color: "#3C2371" }}>{v ?? "—"}</span>,
    },
    {
      title: "Activation",
      key: "activation",
      align: "right",
      render: (_, r) => {
        const our = Number(r.ourActivationPrice || r.activation || 0);
        return (
          <div style={{ textAlign: "right" }}>
            <div style={{ fontWeight: 700, fontSize: 13 }}>${our.toFixed(2)}</div>
          </div>
        );
      },
    },
    {
      title: "Monthly",
      key: "monthly",
      align: "right",
      render: (_, r) => {
        const our = Number(r.ourMonthlyPrice || r.monthly_fee || 0);
        return (
          <div style={{ textAlign: "right" }}>
            <div style={{ fontWeight: 700, color: "#52c41a", fontSize: 14 }}>
              ${our.toFixed(2)}<span style={{ color: "#8c8c8c", fontWeight: 400, fontSize: 11 }}>/mo</span>
            </div>
          </div>
        );
      },
    },
    {
      title: "Per Min",
      key: "per_min",
      align: "right",
      render: (_, r) => (
        <span style={{ fontSize: 12, color: "#8c8c8c" }}>
          {(r.ourPerMinute || 0) > 0 ? `$${Number(r.ourPerMinute).toFixed(3)}` : "—"}
        </span>
      ),
    },
    {
      title: "Documents",
      key: "docs",
      align: "center",
      render: (_, r) => {
        const docs     = r.required_documents || [];
        const fullList = buildDocList(docs, r.country || "");
        if (!fullList.length) {
          return (
            <Tag color="green" style={{ borderRadius: 20, fontSize: 10 }}>
              <i className="ti ti-circle-check" style={{ marginRight: 3 }} />None
            </Tag>
          );
        }
        return (
          <Tooltip title="Click to view required documents">
            <Tag
              color="orange"
              style={{ borderRadius: 20, fontSize: 10, cursor: "pointer" }}
              onClick={() => setDocsModal({ open: true, docs, number: r.number, countryName: r.country || "" })}
            >
              <i className="ti ti-file-certificate" style={{ marginRight: 3 }} />
              {fullList.length} required
            </Tag>
          </Tooltip>
        );
      },
    },
    {
      title: "",
      key: "remove",
      width: 50,
      render: (_, r) => {
        if (purchasedIds.has(r.did_id)) return null;
        return (
          <Tooltip title="Remove from cart">
            <Button
              size="small"
              loading={removing === r.did_id}
              onClick={() => dispatch(removeFromCart(r.did_id))}
              style={{ borderRadius: 8, border: "1.5px solid #ff4d4f", color: "#ff4d4f", background: "#fff2f0" }}
            >
              {removing !== r.did_id && <i className="ti ti-trash" />}
            </Button>
          </Tooltip>
        );
      },
    },
  ];

  return (
    <div className="page-wrapper">
      <div className="content">
        <div className="card" style={{ padding: "20px 24px", borderRadius: 12 }}>

        {/* Page header */}
        <div style={{ display: "flex", alignItems: "center", justifyContent: "space-between", marginBottom: 20, flexWrap: "wrap", gap: 12 }}>
          <div>
            <h4 style={{ fontWeight: 700, marginBottom: 4 }}>
              <i className="ti ti-shopping-cart" style={{ marginRight: 8, color: "#667eea" }} />
              Number Cart
            </h4>
            <p style={{ color: "#8c8c8c", margin: 0, fontSize: 13 }}>
              {items.length} number{items.length !== 1 ? "s" : ""} in cart
            </p>
          </div>
          <div style={{ display: "flex", gap: 10 }}>
            <Button onClick={() => navigate(route.didBrowseNumbers)} style={{ borderRadius: 8 }}>
              <i className="ti ti-plus" style={{ marginRight: 6 }} />Browse More
            </Button>
            {items.length > 0 && (
              <Popconfirm
                title="Clear the entire cart?"
                description="All numbers will be removed. This cannot be undone."
                onConfirm={() => dispatch(clearCart())}
                okText="Yes, Clear Cart"
                okButtonProps={{ danger: true }}
                cancelText="Cancel"
                placement="bottomRight"
              >
                <Button danger style={{ borderRadius: 8 }}>
                  <i className="ti ti-trash" style={{ marginRight: 6 }} />Clear Cart
                </Button>
              </Popconfirm>
            )}
          </div>
        </div>

        {/* Empty state */}
        {!loading && items.length === 0 && (
          <div style={{
            textAlign: "center", padding: "80px 20px",
            background: "#fff", borderRadius: 16, border: "1px solid #f0f0f0",
          }}>
            <i className="ti ti-shopping-cart" style={{ fontSize: 52, color: "#bfbfbf", display: "block", marginBottom: 16 }} />
            <h5 style={{ color: "#595959", fontWeight: 600 }}>Your cart is empty</h5>
            <p style={{ color: "#8c8c8c", fontSize: 13, marginBottom: 20 }}>Browse available numbers and add them to your cart</p>
            <Button
              type="primary"
              onClick={() => navigate(route.didBrowseNumbers)}
              style={{ borderRadius: 8, background: "linear-gradient(135deg, #3C2371, #8e77fa)", border: "none" }}
            >
              <i className="ti ti-world" style={{ marginRight: 6 }} />Browse Numbers
            </Button>
          </div>
        )}

        {(loading || items.length > 0) && (
          <>
            {/* Selection hint */}
            {unpurchasedItems.length > 0 && (
              <div style={{
                display: "flex", alignItems: "center", gap: 10, marginBottom: 8,
                padding: "8px 14px", background: "#f0f4ff", borderRadius: 8, fontSize: 12, color: "#3C2371",
              }}>
                <i className="ti ti-checkbox" />
                <span>
                  <strong>{selectedRowKeys.length}</strong> of {unpurchasedItems.length} selected —
                  use checkboxes to choose which numbers to purchase
                </span>
                {selectedRowKeys.length < unpurchasedItems.length && (
                  <button
                    onClick={() => setSelectedRowKeys(unpurchasedItems.map((i) => i.did_id))}
                    style={{ background: "none", border: "none", color: "#5B6ABF", cursor: "pointer", fontSize: 12, fontWeight: 600, padding: 0 }}
                  >
                    Select all
                  </button>
                )}
                {selectedRowKeys.length > 0 && (
                  <button
                    onClick={() => setSelectedRowKeys([])}
                    style={{ background: "none", border: "none", color: "#8c8c8c", cursor: "pointer", fontSize: 12, padding: 0 }}
                  >
                    Clear
                  </button>
                )}
              </div>
            )}

            {/* Cart table with checkboxes */}
            <div style={{ background: "#fff", borderRadius: 12, border: "1px solid #f0f0f0", overflow: "hidden", marginBottom: 16 }}>
              <Table
                dataSource={items}
                columns={columns}
                rowKey={(r) => r.did_id || r.number}
                loading={loading}
                pagination={false}
                scroll={{ x: 860 }}
                rowClassName={(r) => purchasedIds.has(r.did_id) ? "row-purchased" : ""}
                rowSelection={{
                  selectedRowKeys,
                  onChange: (keys) => setSelectedRowKeys(keys),
                  // Prevent selecting already-purchased rows
                  getCheckboxProps: (r) => ({
                    disabled: purchasedIds.has(r.did_id),
                    style: purchasedIds.has(r.did_id) ? { display: "none" } : {},
                  }),
                  columnWidth: 48,
                }}
                locale={{ emptyText: <div style={{ padding: 40 }}><Spin /></div> }}
              />
            </div>

            {/* Totals + Purchase Selected */}
            {selectedItems.length > 0 && (
              <div style={{
                background: "linear-gradient(135deg, #3C2371 0%, #5B6ABF 55%, #8e77fa 100%)",
                borderRadius: 12, padding: "18px 24px",
                display: "flex", alignItems: "center", justifyContent: "space-between",
                flexWrap: "wrap", gap: 14, color: "#fff",
              }}>
                <div style={{ display: "flex", gap: 28 }}>
                  <div>
                    <div style={{ fontSize: 11, color: "rgba(255,255,255,0.65)", marginBottom: 2 }}>ACTIVATION</div>
                    <div style={{ fontSize: 20, fontWeight: 700 }}>${totalActivation.toFixed(2)}</div>
                  </div>
                  <div>
                    <div style={{ fontSize: 11, color: "rgba(255,255,255,0.65)", marginBottom: 2 }}>MONTHLY</div>
                    <div style={{ fontSize: 20, fontWeight: 700 }}>
                      ${totalMonthly.toFixed(2)}<span style={{ fontSize: 12, fontWeight: 400, opacity: 0.7 }}>/mo</span>
                    </div>
                  </div>
                  <div>
                    <div style={{ fontSize: 11, color: "rgba(255,255,255,0.65)", marginBottom: 2 }}>SELECTED</div>
                    <div style={{ fontSize: 20, fontWeight: 700 }}>{selectedItems.length}</div>
                  </div>
                </div>

                <Button
                  size="large"
                  onClick={() => setBulkModalOpen(true)}
                  style={{
                    background: "#fff", color: "#3C2371",
                    border: "none", borderRadius: 10,
                    fontWeight: 700, fontSize: 14, padding: "0 28px",
                    height: 44,
                  }}
                >
                  <i className="ti ti-shopping-cart" style={{ marginRight: 8 }} />
                  Purchase {selectedItems.length} Selected Number{selectedItems.length !== 1 ? "s" : ""}
                </Button>
              </div>
            )}

            {/* No selection warning */}
            {unpurchasedItems.length > 0 && selectedItems.length === 0 && (
              <div style={{
                textAlign: "center", padding: "12px",
                background: "#fffbe6", border: "1px solid #ffe58f",
                borderRadius: 10, fontSize: 13, color: "#613400",
              }}>
                <i className="ti ti-info-circle" style={{ marginRight: 6 }} />
                Select at least one number above to purchase
              </div>
            )}
          </>
        )}

        {/* Purchased row highlight */}
        <style>{`.row-purchased > td { background: #f6ffed !important; opacity: 0.7; }`}</style>

        {/* Modals */}
        <DocsModal
          open={docsModal.open}
          docs={docsModal.docs}
          number={docsModal.number}
          countryName={docsModal.countryName}
          onClose={() => setDocsModal({ open: false, docs: [], number: "", countryName: "" })}
        />
        <BulkPurchaseModal
          open={bulkModalOpen}
          items={selectedItems}
          onClose={handleBulkClose}
        />
        </div>{/* end card */}
      </div>
    </div>
  );
};

export default NumberCart;
